// src/pages/TripManagement.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
   collection,
   getDocs,
   addDoc,
   updateDoc,
   doc,
   deleteDoc,
   Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { format } from 'date-fns';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
   Box,
   Paper,
   Typography,
   Button,
   TextField,
   Grid,
   Card,
   CardMedia,
   CardContent,
   CardActions,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   IconButton,
   Chip,
   Divider,
   List,
   ListItem,
   ListItemText,
   ListItemAvatar,
   Avatar,
   Tabs,
   Tab,
   CircularProgress,
   FormControl,
   InputLabel,
   OutlinedInput,
   InputAdornment,
   ListItemIcon,
   Select,
   MenuItem,
   Autocomplete,
   ListItemButton,
   Snackbar,
   Alert
} from '@mui/material';
import {
   Add as AddIcon,
   Edit as EditIcon,
   Delete as DeleteIcon,
   LocationOn as LocationIcon,
   PhotoCamera as PhotoIcon,
   Share as ShareIcon,
   Person as PersonIcon,
   PersonAdd as PersonAddIcon,
   Email as EmailIcon,
   Check as CheckIcon,
   Search as SearchIcon,
   Place as PlaceIcon,
   Phone as PhoneIcon,
} from '@mui/icons-material';
import CoverImageUpload from '../components/common/CoverImageUpload';
import { useUser } from '../contexts/UserContext';
import tripSharingService from '../services/TripSharingService';
import { useNavigate } from 'react-router-dom';
import { City, searchCities, searchLocations, cityToLocationResult } from '../data/cities';
import LocationSearchExample from '../components/common/LocationSearchExample';
import TripLocationPicker from '../components/common/TripLocationPicker';
import { LocationResult } from '../components/common/LocationSearch';
import AccommodationForm from '../components/common/AccommodationForm';
import { Accommodation } from '../types/Trip';
import CreateTripForm from '../components/common/CreateTripForm';
import SMSInviteForm from '../components/SMSInviteForm';

interface Trip {
   id: string;
   title: string;
   description: string;
   startDate: Date;
   endDate: Date;
   photoCount?: number;
   coverPhotoUrl?: string;
   coverImageUrl?: string;
   primaryDestination?: {
      name: string;
      coordinates: [number, number];
      country: string;
   };
   locations: {
      name: string;
      coordinates: [number, number];
   }[];
   accommodations?: Accommodation[];
   collaborators: {
      id: string;
      email: string;
      name?: string;
      status: 'owner' | 'viewer' | 'editor';
      isAccepted: boolean;
      userName: string;
      userPhotoURL: string;
   }[];
   tags: string[];
}

interface PlacePrediction {
   place_id: string;
   description: string;
   structured_formatting?: {
      main_text: string;
      secondary_text: string;
   };
   [key: string]: any;
}

interface PlaceDetails {
   name?: string;
   geometry?: {
      location: {
         lat: () => number;
         lng: () => number;
      }
   };
   address_components?: Array<{
      long_name: string;
      short_name: string;
      types: string[];
   }>;
   [key: string]: any;
}

// Add type definitions for Google Maps callbacks
interface AutocompletePrediction {
   place_id: string;
   description: string;
   structured_formatting?: {
      main_text: string;
      secondary_text: string;
   };
   [key: string]: any;
}

const TripManagement: React.FC = () => {
   const [trips, setTrips] = useState<any[]>([]);
   const [invitedTrips, setInvitedTrips] = useState<any[]>([]);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
   const [locationDialogOpen, setLocationDialogOpen] = useState(false);
   const [locationSearchInput, setLocationSearchInput] = useState("");
   const [locationResults, setLocationResults] = useState<
      Array<{
         place_id?: string;
         description: string;
         name: string;
         coordinates: [number, number];
         country: string;
      }>
   >([]);
   const [isLoadingPlaces, setIsLoadingPlaces] = useState(false);
   
   const [tabValue, setTabValue] = useState(0);
   const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
   const navigate = useNavigate();
   
   const [inviteTabValue, setInviteTabValue] = useState(0);
   const { currentUser } = useUser();
   
   // Add missing state variables
   const [tripForm, setTripForm] = useState({
      id: '',
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      coverPhotoUrl: '',
      coverImageUrl: '',
      primaryDestination: {
         name: '',
         coordinates: [0, 0] as [number, number],
         country: '',
      },
      accommodations: [] as Accommodation[]
   });

   const [newLocation, setNewLocation] = useState({
      name: '',
      coordinates: [0, 0] as [number, number],
   });
   
   const [snackbar, setSnackbar] = useState<{
      open: boolean;
      message: string;
      severity: 'success' | 'error' | 'info' | 'warning';
   }>({
      open: false,
      message: '',
      severity: 'info'
   });

   // Dialog states
   const [createDialogOpen, setCreateDialogOpen] = useState(false);
   const [editDialogOpen, setEditDialogOpen] = useState(false);
   const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
   
   // More form states
   const [inviteEmail, setInviteEmail] = useState('');
   const [inviteeRole, setInviteeRole] = useState('viewer');

   // Map refs
   const tripMapContainer = useRef<HTMLDivElement>(null);
   const tripMap = useRef<mapboxgl.Map | null>(null);
   const locationMapContainer = useRef<HTMLDivElement>(null);
   const locationMap = useRef<mapboxgl.Map | null>(null);
   const locationMarker = useRef<mapboxgl.Marker | null>(null);

   // Add a new state to track if a location has been properly selected
   const [locationSelected, setLocationSelected] = useState(false);

   // Add a reference for the input element
   const locationInputRef = useRef<HTMLInputElement>(null);
   const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

   // Add a new state to trigger trip saving
   const [shouldSaveTrip, setShouldSaveTrip] = useState(false);

   // Create a stable version of handleSaveNewTrip with useCallback
   const handleSaveNewTrip = useCallback(async () => {
      if (!tripForm.title || !tripForm.startDate || !tripForm.endDate) {
         setSnackbar({
            open: true,
            message: 'Please fill in all required fields (title, start date, end date)',
            severity: 'error',
         });
         return;
      }

      try {
         setLoading(true);

         // Create a base trip object without optional fields
         const newTrip: any = {
            owner: currentUser?.uid,
            title: tripForm.title,
            description: tripForm.description || '', // Ensure it's never undefined
            startDate: Timestamp.fromDate(new Date(tripForm.startDate)),
            endDate: Timestamp.fromDate(new Date(tripForm.endDate)),
            locations: [],
            accommodations: tripForm.accommodations || [],
            collaborators: [
               {
                  id: currentUser?.uid,
                  email: currentUser?.email,
                  status: 'owner',
                  isAccepted: true,
                  userName: currentUser?.displayName || '',
                  userPhotoURL: currentUser?.photoURL || null, // Use null instead of undefined
               },
            ],
            tags: [],
         };

         // Only add optional fields if they exist
         if (tripForm.coverPhotoUrl) {
            newTrip.coverPhotoUrl = tripForm.coverPhotoUrl;
         }

         // Only add primaryDestination if it exists and has all required properties
         if (
            tripForm.primaryDestination &&
            tripForm.primaryDestination.name &&
            tripForm.primaryDestination.coordinates &&
            tripForm.primaryDestination.country
         ) {
            newTrip.primaryDestination = tripForm.primaryDestination;
         }

         // Use backend API to create trip with proper members array
         const { createApiUrl } = await import('../utils/apiConfig');
         const { getAuthHeaders } = await import('../utils/auth');
         const headers = await getAuthHeaders();
         const apiUrl = createApiUrl('/api/trips');

         const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({
               title: tripForm.title,
               description: tripForm.description || '',
               startDate: tripForm.startDate,
               endDate: tripForm.endDate,
               isPublic: false,
               primaryDestination: tripForm.primaryDestination,
               coverPhotoUrl: tripForm.coverPhotoUrl,
               userId: currentUser?.uid,
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to create trip');
         }

         const docRef = { id: result.tripId };

         setTrips([
            {
               ...tripForm,
               id: docRef.id,
               owner: currentUser?.uid,
               title: tripForm.title,
               description: tripForm.description || '',
               startDate: new Date(tripForm.startDate),
               endDate: new Date(tripForm.endDate),
               primaryDestination: tripForm.primaryDestination,
               accommodations: tripForm.accommodations || [],
            },
            ...trips,
         ]);

         // Close dialog and show success message
         setTripForm({
            id: '',
            title: '',
            description: '',
            startDate: '',
            endDate: '',
            coverPhotoUrl: '',
            coverImageUrl: '',
            primaryDestination: {
               name: '',
               coordinates: [0, 0] as [number, number],
               country: '',
            },
            accommodations: [],
         });
         setCreateDialogOpen(false);
         setLoading(false);

         // Show success notification
         setSnackbar({
            open: true,
            message: `Trip "${tripForm.title}" successfully created!`,
            severity: 'success',
         });
      } catch (error) {
         console.error('Error adding document: ', error);
         setLoading(false);

         // Show error notification
         setSnackbar({
            open: true,
            message: 'Failed to save trip. Please try again.',
            severity: 'error',
         });
      }
   }, [
      tripForm,
      currentUser,
      trips,
      setTrips,
      setTripForm,
      setCreateDialogOpen,
      setLoading,
      setSnackbar,
   ]);

   // Update the useEffect
   useEffect(() => {
      const saveTrip = async () => {
         if (shouldSaveTrip) {
            await handleSaveNewTrip();
            setShouldSaveTrip(false);
         }
      };

      saveTrip();
   }, [shouldSaveTrip, handleSaveNewTrip]);

   // Add a handler for the LocationResult from our new component
   const handleLocationSelectFromPicker = (location: LocationResult) => {
      setTripForm({
         ...tripForm,
         primaryDestination: {
            name: location.mainText,
            country: location.secondaryText,
            coordinates: [location.coordinates.lng, location.coordinates.lat] as [number, number],
         },
      });

      // Mark location as selected
      setLocationSelected(true);
   };

   const handleOpenTrip = (id: string) => {
      navigate(`/timeline?trip_id=${id}`);
   };

   const fetchTrips = async () => {
      try {
         const tripsCollection = collection(db, 'trips');
         const tripSnapshot = await getDocs(tripsCollection);

         const tripData: Trip[] = [];
         const sharedTripData: Trip[] = [];

         tripSnapshot.forEach((doc) => {
            const data = doc.data();

            if (
               data.collaborators.some(
                  (c: any) => c?.email === currentUser?.email && c?.status === 'owner'
               )
            ) {
               const trip: Trip = {
                  id: doc.id,
                  title: data.title,
                  description: data.description || '',
                  startDate: data.startDate.toDate(),
                  endDate: data.endDate.toDate(),
                  coverPhotoUrl: data.coverPhotoUrl,
                  coverImageUrl: data.coverImageUrl,
                  primaryDestination: data.primaryDestination,
                  locations: data.locations || [],
                  accommodations: data.accommodations || [],
                  collaborators: data.collaborators || [],
                  tags: data.tags || [],
                  photoCount: data.photoCount || 0,
               };

               tripData.push(trip);
            }

            if (
               data.collaborators.some(
                  (c: any) => c?.email === currentUser?.email && c?.status !== 'owner'
               )
            ) {
               const trip: Trip = {
                  id: doc.id,
                  title: data.title,
                  description: data.description || '',
                  startDate: data.startDate.toDate(),
                  endDate: data.endDate.toDate(),
                  coverPhotoUrl: data.coverPhotoUrl,
                  coverImageUrl: data.coverImageUrl,
                  primaryDestination: data.primaryDestination,
                  locations: data.locations || [],
                  accommodations: data.accommodations || [],
                  collaborators: data.collaborators || [],
                  tags: data.tags || [],
                  photoCount: data.photoCount || 0,
               };

               sharedTripData.push(trip);
            }
         });

         setTrips(tripData.sort((a, b) => b.startDate.getTime() - a.startDate.getTime()));
         setInvitedTrips(
            sharedTripData.sort((a, b) => b.startDate.getTime() - a.startDate.getTime())
         );
         setLoading(false);
      } catch (error) {
         console.error('Error fetching trips:', error);
         setLoading(false);
      }
   };

   useEffect(() => {
      fetchTrips();
   }, []);

   useEffect(() => {
      fetchTrips();
   }, [inviteDialogOpen]);

   // Initialize trip map
   useEffect(() => {
      if (!tripMapContainer.current || trips.length === 0) return;

      if (tripMap.current) return;

      // Disable map if no valid token to prevent errors
      if (!process.env.REACT_APP_MAPBOX_ACCESS_TOKEN) {
         console.warn('Mapbox token not found. Map features disabled.');
         return;
      }

      try {
         // Set token here
         mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

         tripMap.current = new mapboxgl.Map({
            container: tripMapContainer.current,
            style: 'mapbox://styles/mapbox/streets-v11',
            center: [0, 20],
            zoom: 1.5,
         });

         tripMap.current.on('load', () => {
            if (!tripMap.current) return;

            try {
               // Add navigation controls
               tripMap.current.addControl(new mapboxgl.NavigationControl());

               // Add markers for all trip locations
               let allLocations: {
                  coordinates: [number, number];
                  trip: Trip;
               }[] = [];

               trips?.forEach((trip) => {
                  trip?.locations?.forEach((location: any) => {
                     allLocations.push({
                        coordinates: location.coordinates,
                        trip: trip,
                     });
                  });
               });

               // Add markers
               allLocations.forEach((location) => {
                  // Create marker element
                  const el = document.createElement('div');
                  el.className = 'trip-marker';
                  el.style.width = '15px';
                  el.style.height = '15px';
                  el.style.borderRadius = '50%';
                  el.style.backgroundColor = '#3498db';
                  el.style.border = '2px solid white';
                  el.style.cursor = 'pointer';

                  // Create popup
                  const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
                <div style="max-width: 200px;">
                  <strong>${location.trip.title}</strong>
                  <p>${format(location.trip.startDate, 'MMM d')} - ${format(
                     location.trip.endDate,
                     'MMM d, yyyy'
                  )}</p>
                </div>
              `);

                  // Add marker to map
                  new mapboxgl.Marker(el)
                     .setLngLat(location.coordinates)
                     .setPopup(popup)
                     .addTo(tripMap.current!);
               });

               // Fit bounds if we have locations
               if (allLocations.length > 0) {
                  const bounds = new mapboxgl.LngLatBounds();
                  allLocations.forEach((location) => {
                     bounds.extend(location.coordinates);
                  });

                  tripMap.current.fitBounds(bounds, {
                     padding: 50,
                     maxZoom: 10,
                  });
               }
            } catch (error) {
               console.error('Error setting up map markers:', error);
            }
         });

         tripMap.current.on('error', (e) => {
            console.error('Mapbox error:', e);
         });
      } catch (error) {
         console.error('Error initializing trip map:', error);
      }
   }, [trips, loading]);

   // Initialize location map for adding/editing locations
   useEffect(() => {
      if (!locationDialogOpen || !locationMapContainer.current) return;

      // Disable map if no valid token to prevent errors
      if (!process.env.REACT_APP_MAPBOX_ACCESS_TOKEN) {
         console.warn('Mapbox token not found. Map features disabled.');
         return;
      }

      const initMap = setTimeout(() => {
         try {
            // Set token here
            mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

            locationMap.current = new mapboxgl.Map({
               container: locationMapContainer.current!,
               style: 'mapbox://styles/mapbox/streets-v11',
               center: newLocation.coordinates,
               zoom: newLocation.coordinates[0] !== 0 ? 10 : 1,
            });

            locationMap.current.on('load', () => {
               if (!locationMap.current) return;

               try {
                  // Add navigation controls
                  locationMap.current.addControl(new mapboxgl.NavigationControl());

                  // Add marker if we have coordinates
                  if (newLocation.coordinates[0] !== 0 || newLocation.coordinates[1] !== 0) {
                     locationMarker.current = new mapboxgl.Marker({ draggable: true })
                        .setLngLat(newLocation.coordinates)
                        .addTo(locationMap.current);

                     locationMarker.current.on('dragend', () => {
                        const lngLat = locationMarker.current?.getLngLat();
                        if (lngLat) {
                           setNewLocation({
                              ...newLocation,
                              coordinates: [lngLat.lng, lngLat.lat],
                           });
                        }
                     });
                  }

                  // Add click handler
                  locationMap.current.on('click', (e) => {
                     if (locationMarker.current) {
                        locationMarker.current.setLngLat(e.lngLat);
                     } else {
                        locationMarker.current = new mapboxgl.Marker({ draggable: true })
                           .setLngLat(e.lngLat)
                           .addTo(locationMap.current!);

                        locationMarker.current.on('dragend', () => {
                           const lngLat = locationMarker.current?.getLngLat();
                           if (lngLat) {
                              setNewLocation({
                                 ...newLocation,
                                 coordinates: [lngLat.lng, lngLat.lat],
                              });
                           }
                        });
                     }

                     setNewLocation({
                        ...newLocation,
                        coordinates: [e.lngLat.lng, e.lngLat.lat],
                     });
                  });
               } catch (error) {
                  console.error('Error setting up map controls:', error);
               }
            });

            locationMap.current.on('error', (err) => {
               console.error('Mapbox error:', err);
            });
         } catch (error) {
            console.error('Error creating location map:', error);
         }
      }, 300);

      // Cleanup function
      return () => {
         clearTimeout(initMap);
         if (locationMap.current) {
            locationMap.current.remove();
         }
      };
   }, [locationDialogOpen, newLocation.coordinates]);

   // Tab change handler
   const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setTabValue(newValue);
   };

   // Trip form handlers
   const handleCreateTrip = () => {
      setTripForm({
         id: '',
         title: '',
         description: '',
         startDate: '',
         endDate: '',
         coverPhotoUrl: '',
         coverImageUrl: '',
         primaryDestination: {
            name: '',
            coordinates: [0, 0] as [number, number],
            country: '',
         },
         accommodations: [],
      });
      setLocationDialogOpen(true);
   };

   const handleEditTrip = (trip: Trip) => {
      setSelectedTrip(trip);
      setTripForm({
         id: trip.id,
         title: trip.title,
         description: trip.description || '',
         startDate: format(trip.startDate, 'yyyy-MM-dd'),
         endDate: format(trip.endDate, 'yyyy-MM-dd'),
         coverPhotoUrl: trip.coverPhotoUrl || '',
         coverImageUrl: trip.coverImageUrl || '',
         primaryDestination: trip.primaryDestination || {
            name: '',
            coordinates: [0, 0] as [number, number],
            country: '',
         },
         accommodations: trip.accommodations || [],
      });
      setEditDialogOpen(true);
   };

   const handleUpdateTrip = async () => {
      try {
         setLoading(true);

         // Update trip in database
         await updateDoc(doc(db, 'trips', tripForm.id), {
            title: tripForm.title,
            description: tripForm.description,
            startDate: new Date(tripForm.startDate),
            endDate: new Date(tripForm.endDate),
            coverPhotoUrl: tripForm.coverPhotoUrl,
            coverImageUrl: tripForm.coverImageUrl,
            accommodations: tripForm.accommodations,
            updatedAt: new Date(),
         });

         // Update in local state
         setTrips((prev) =>
            prev.map((trip) =>
               trip.id === tripForm.id
                  ? {
                       ...trip,
                       title: tripForm.title,
                       description: tripForm.description,
                       startDate: new Date(tripForm.startDate),
                       endDate: new Date(tripForm.endDate),
                       coverPhotoUrl: tripForm.coverPhotoUrl,
                       coverImageUrl: tripForm.coverImageUrl,
                       accommodations: tripForm.accommodations,
                    }
                  : trip
            )
         );

         setEditDialogOpen(false);
         setLoading(false);

         // Show success message
         setSnackbar({
            open: true,
            message: 'Trip updated successfully',
            severity: 'success',
         });
      } catch (error) {
         console.error('Error updating trip:', error);
         setLoading(false);

         // Show error message
         setSnackbar({
            open: true,
            message: 'Failed to update trip. Please try again.',
            severity: 'error',
         });
      }
   };

   const handleDeleteTrip = (trip: Trip) => {
      setSelectedTrip(trip);
      setDeleteDialogOpen(true);
   };

   const handleConfirmDelete = async () => {
      if (!selectedTrip) return;

      try {
         // Use backend API to delete trip
         const { createApiUrl } = await import('../utils/apiConfig');
         // Use current user ID from auth context instead of trip.userId
         const userId = (selectedTrip as any).userId || 'current-user-id';
         const apiUrl = createApiUrl(`/api/trips/${selectedTrip.id}?userId=${userId}`);

         const response = await fetch(apiUrl, {
            method: 'DELETE',
            headers: {
               'Content-Type': 'application/json',
            },
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to delete trip');
         }

         // Update local state
         setTrips(trips.filter((trip) => trip.id !== selectedTrip.id));

         setDeleteDialogOpen(false);
         setSelectedTrip(null);
      } catch (error) {
         console.error('Error deleting trip:', error);
      }
   };

   // Location handlers
   const handleAddLocation = (trip: Trip) => {
      setSelectedTrip(trip);
      setNewLocation({
         name: '',
         coordinates: [0, 0],
      });
      setLocationDialogOpen(true);
   };

   const handleSaveLocation = async () => {
      if (!selectedTrip || !newLocation.name || !newLocation.coordinates) return;

      try {
         // Update trip with new location
         const updatedLocations = [...selectedTrip.locations, newLocation];

         await updateDoc(doc(db, 'trips', selectedTrip.id), {
            locations: updatedLocations,
         });

         // Update local state
         setTrips(
            trips.map((trip) =>
               trip.id === selectedTrip.id
                  ? {
                       ...trip,
                       locations: updatedLocations,
                    }
                  : trip
            )
         );

         setLocationDialogOpen(false);
         setSelectedTrip(null);
      } catch (error) {
         console.error('Error adding location:', error);
      }
   };

   // Invite collaborator handler
   const handleInviteCollaborator = (trip: Trip) => {
      setSelectedTrip(trip);
      setInviteEmail('');
      setInviteTabValue(0); // Default to email tab
      setInviteDialogOpen(true);
   };

   const handleInviteTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setInviteTabValue(newValue);
   };

   const handleSendInvite = async () => {
      if (!selectedTrip || !inviteEmail) {
         alert('Please input the email address');
         return;
      }

      try {
         await tripSharingService.inviteUserToTrip(
            selectedTrip.id,
            inviteEmail,
            inviteeRole,
            currentUser?.displayName || ''
         );
         setInviteDialogOpen(false);
      } catch (error) {
         console.error('Error inviting collaborator:', error);
      }
   };

   const handleSMSInviteSent = () => {
      // Close the dialog after sending SMS invite
      setInviteDialogOpen(false);
   };

   const uploadImage = async (file: File): Promise<string> => {
      const storage = getStorage();
      const storageRef = ref(storage, `trip-covers/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      return getDownloadURL(snapshot.ref);
   };

   const handleCoverImageUpload = async (file: File) => {
      setLoading(true);
      try {
         const uploadedUrl = await uploadImage(file);
         setTripForm((prev) => ({
            ...prev,
            coverPhotoUrl: uploadedUrl,
         }));
      } catch (error) {
      } finally {
         setLoading(false);
      }
   };

   // Update the renderCreateDialog function with proper typing and data handling
   const renderCreateDialog = () => {
      // Define proper interface for tripData from CreateTripForm
      interface CreateFormData {
         title: string;
         startDate: Date | null;
         endDate: Date | null;
         destination: {
            placeId?: string;
            mainText: string;
            secondaryText: string;
            coordinates: [number, number] | { lat: number; lng: number };
         } | null;
         coverImage: string | null;
         accommodations: Accommodation[];
      }

      const handleSaveTrip = async (formData: CreateFormData) => {
         try {
            setLoading(true);

            // Extract coordinates from the trip data
            let coordinates: [number, number] = [0, 0];
            if (formData.destination?.coordinates) {
               if (Array.isArray(formData.destination.coordinates)) {
                  coordinates = formData.destination.coordinates;
               } else {
                  coordinates = [
                     formData.destination.coordinates.lng,
                     formData.destination.coordinates.lat,
                  ];
               }
            }

            // Validate that we have the required fields
            if (!formData.title) {
               throw new Error('Title is required');
            }

            if (!formData.startDate || !formData.endDate) {
               throw new Error('Start and end dates are required');
            }

            // Format dates correctly
            const startDate = formData.startDate ? new Date(formData.startDate) : null;
            const endDate = formData.endDate ? new Date(formData.endDate) : null;

            if (!startDate || !endDate || isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
               throw new Error('Invalid date format');
            }

            // Set destination name and country
            const destName = formData.destination?.mainText || tripForm.primaryDestination.name;
            const destCountry =
               formData.destination?.secondaryText || tripForm.primaryDestination.country;

            // Update the form state
            setTripForm({
               ...tripForm,
               title: formData.title,
               startDate: startDate.toISOString(),
               endDate: endDate.toISOString(),
               coverPhotoUrl: formData.coverImage || tripForm.coverPhotoUrl,
               coverImageUrl: formData.coverImage || tripForm.coverImageUrl,
               primaryDestination: {
                  name: destName,
                  coordinates: formData.destination
                     ? coordinates
                     : tripForm.primaryDestination.coordinates,
                  country: destCountry,
               },
               accommodations: formData.accommodations || tripForm.accommodations,
            });

            // Trigger save operation
            setShouldSaveTrip(true);
         } catch (error) {
            console.error('Error preparing trip data:', error);
            setLoading(false);
            setSnackbar({
               open: true,
               message: error instanceof Error ? error.message : 'Failed to prepare trip data',
               severity: 'error',
            });
         }
      };

      return (
         <CreateTripForm
            open={createDialogOpen}
            onClose={() => setCreateDialogOpen(false)}
            onSave={handleSaveTrip}
         />
      );
   };

   // Add this function before the return statement
   const renderLocationDialog = () => (
      <Dialog
         open={locationDialogOpen}
         onClose={() => setLocationDialogOpen(false)}
         maxWidth="sm"
         fullWidth
      >
         <DialogTitle>Set Primary Destination</DialogTitle>
         <DialogContent>
            <LocationSearchExample 
               onSave={(location) => {
                  // Update tripForm with the selected location
                  setTripForm({
                     ...tripForm,
                     primaryDestination: {
                        name: location.mainText,
                        country: location.secondaryText,
                        coordinates: [location.coordinates.lng, location.coordinates.lat] as [number, number]
                     }
                  });
                  
                  // Mark location as selected
                  setLocationSelected(true);
                  
                  // Close the dialog
                  setLocationDialogOpen(false);
               }}
               onClose={() => setLocationDialogOpen(false)}
               title="Set Primary Destination"
            />
         </DialogContent>
      </Dialog>
   );

   // Add the handleAccommodationsChange function
   const handleAccommodationsChange = (accommodations: Accommodation[]) => {
      setTripForm({
         ...tripForm,
         accommodations
      });
   };

   return (
      <Box sx={{ padding: 3 }}>
         <Paper sx={{ p: 2, mb: 3 }}>
            <Box
               sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
               }}
            >
               <Typography variant='h5'>Trip Management</Typography>
               <Button variant='contained' startIcon={<AddIcon />} onClick={handleCreateTrip}>
                  Create New Trip
               </Button>
            </Box>

            <Tabs
               value={tabValue}
               onChange={handleTabChange}
               sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
               <Tab label='My Trips' id='tab-0' />
               <Tab label='Shared With Me' id='tab-1' />
               <Tab label='Map View' id='tab-2' />
            </Tabs>
         </Paper>

         <div role='tabpanel' hidden={tabValue !== 0}>
            {loading ? (
               <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
               </Box>
            ) : trips.length === 0 ? (
               <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant='h6' color='text.secondary' gutterBottom>
                     You haven't created any trips yet
                  </Typography>
                  <Button
                     variant='contained'
                     startIcon={<AddIcon />}
                     onClick={handleCreateTrip}
                     sx={{ mt: 2 }}
                  >
                     Create Your First Trip
                  </Button>
               </Paper>
            ) : (
               <Grid container spacing={3}>
                  {trips.map((trip) => (
                     <Grid item xs={12} md={6} lg={4} key={trip.id}>
                        <Card>
                           <CardMedia
                              style={{
                                 cursor: 'pointer',
                              }}
                              onClick={() => handleOpenTrip(trip.id)}
                              component='img'
                              height='160'
                              image={
                                 trip.coverPhotoUrl || trip.coverImageUrl || 'https://source.unsplash.com/random/?travel'
                              }
                              alt={trip.title}
                           />
                           <CardContent>
                              <Typography
                                 style={{
                                    cursor: 'pointer',
                                 }}
                                 onClick={() => handleOpenTrip(trip.id)}
                                 variant='h6'
                                 component='div'
                              >
                                 {trip.title}
                              </Typography>
                              <Typography variant='body2' color='text.secondary' gutterBottom>
                                 {format(trip.startDate, 'MMM d')} -{' '}
                                 {format(trip.endDate, 'MMM d, yyyy')}
                              </Typography>

                              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                 <Chip
                                    icon={<PlaceIcon />}
                                    label={trip.primaryDestination?.name || 'Add Destination'}
                                    color={trip.primaryDestination ? 'primary' : 'default'}
                                    onClick={() => {
                                       setSelectedTrip(trip);
                                       setTripForm({
                                          ...tripForm,
                                          primaryDestination: trip.primaryDestination || {
                                             name: '',
                                             coordinates: [0, 0],
                                             country: '',
                                          },
                                       });
                                       setLocationDialogOpen(true);
                                    }}
                                    variant={trip.primaryDestination ? 'filled' : 'outlined'}
                                 />
                                 {trip.photoCount !== undefined && (
                                    <Chip
                                       icon={<PhotoIcon />}
                                       label={`${trip?.photoCount} Photos`}
                                       variant='outlined'
                                    />
                                 )}
                              </Box>

                              {trip.description && (
                                 <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                                    {trip.description}
                                 </Typography>
                              )}
                           </CardContent>
                           <Divider />
                           <CardActions sx={{ justifyContent: 'space-between' }}>
                              <Box>
                                 <IconButton size='small' onClick={() => handleEditTrip(trip)}>
                                    <EditIcon fontSize='small' />
                                 </IconButton>
                                 <IconButton
                                    size='small'
                                    color='error'
                                    onClick={() => handleDeleteTrip(trip)}
                                 >
                                    <DeleteIcon fontSize='small' />
                                 </IconButton>
                              </Box>
                              <Box>
                                 <Button
                                    size='small'
                                    startIcon={<LocationIcon />}
                                    onClick={() => handleAddLocation(trip)}
                                 >
                                    Add Location
                                 </Button>
                                 <Button
                                    size='small'
                                    startIcon={<ShareIcon />}
                                    onClick={() => handleInviteCollaborator(trip)}
                                 >
                                    Invite
                                 </Button>
                              </Box>
                           </CardActions>
                        </Card>
                     </Grid>
                  ))}
               </Grid>
            )}
         </div>

         <div role='tabpanel' hidden={tabValue !== 1}>
            {invitedTrips.length === 0 && (
               <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant='h6' color='text.secondary' gutterBottom>
                     Trips shared with you will appear here
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                     When someone invites you to collaborate on a trip, you'll see it here.
                  </Typography>
               </Paper>
            )}

            <Grid container spacing={3}>
               {invitedTrips?.map((trip) => (
                  <Grid item xs={12} md={6} lg={4} key={trip.id}>
                     <Card>
                        <CardMedia
                           style={{
                              cursor: 'pointer',
                           }}
                           onClick={() => handleOpenTrip(trip.id)}
                           component='img'
                           height='160'
                           image={
                              trip.coverPhotoUrl || trip.coverImageUrl || 'https://source.unsplash.com/random/?travel'
                           }
                           alt={trip.title}
                        />
                        <CardContent>
                           <Typography
                              style={{
                                 cursor: 'pointer',
                              }}
                              onClick={() => handleOpenTrip(trip.id)}
                              variant='h6'
                              component='h1'
                           >
                              {trip.title}
                           </Typography>
                           <Typography variant='body2' color='text.secondary' gutterBottom>
                              {format(trip.startDate, 'MMM d')} -{' '}
                              {format(trip.endDate, 'MMM d, yyyy')}
                           </Typography>

                           <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {trip.photoCount !== undefined && (
                                 <Chip
                                    icon={<PhotoIcon />}
                                    label={`${trip.photoCount} Photos`}
                                    variant='outlined'
                                 />
                              )}
                           </Box>

                           {trip.description && (
                              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                                 {trip.description}
                              </Typography>
                           )}
                        </CardContent>
                     </Card>
                  </Grid>
               ))}
            </Grid>
         </div>

         <div role='tabpanel' hidden={tabValue !== 2}>
            <Paper sx={{ p: 2 }}>
               <Typography variant='h6' gutterBottom>
                  Trip Map View
               </Typography>
               <Box
                  ref={tripMapContainer}
                  sx={{
                     height: 500,
                     borderRadius: 1,
                     border: '1px solid #ddd',
                  }}
               />
            </Paper>
         </div>

         {/* Create Trip Dialog */}
         {renderCreateDialog()}

         {/* Edit Trip Dialog */}
         <Dialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            maxWidth='md'
            fullWidth
         >
            <DialogTitle>Edit Trip</DialogTitle>
            <DialogContent>
               <TextField
                  label='Trip Title'
                  fullWidth
                  margin='normal'
                  value={tripForm.title}
                  onChange={(e) => setTripForm({ ...tripForm, title: e.target.value })}
                  required
               />
               <TextField
                  label='Description'
                  fullWidth
                  margin='normal'
                  multiline
                  rows={3}
                  value={tripForm.description}
                  onChange={(e) => setTripForm({ ...tripForm, description: e.target.value })}
               />
               <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                     label='Start Date'
                     type='date'
                     fullWidth
                     InputLabelProps={{ shrink: true }}
                     value={tripForm.startDate}
                     onChange={(e) => setTripForm({ ...tripForm, startDate: e.target.value })}
                     required
                  />
                  <TextField
                     label='End Date'
                     type='date'
                     fullWidth
                     InputLabelProps={{ shrink: true }}
                     value={tripForm.endDate}
                     onChange={(e) => setTripForm({ ...tripForm, endDate: e.target.value })}
                     required
                  />
               </Box>
               
               {/* Add AccommodationForm component */}
               <Box sx={{ mt: 3 }}>
                  <AccommodationForm 
                     accommodations={tripForm.accommodations}
                     onChange={handleAccommodationsChange}
                     tripStartDate={tripForm.startDate}
                     tripEndDate={tripForm.endDate}
                  />
               </Box>
               
               <CoverImageUpload
                  coverPhotoUrl={tripForm.coverPhotoUrl || tripForm.coverImageUrl}
                  onImageChange={(url) => setTripForm({ ...tripForm, coverPhotoUrl: url })}
                  onImageUpload={handleCoverImageUpload}
                  loading={loading}
                  title='Trip Cover Image'
                  height={200}
               />
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
               <Button
                  onClick={handleUpdateTrip}
                  variant='contained'
                  disabled={!tripForm.title || !tripForm.startDate || !tripForm.endDate}
               >
                  Save Changes
               </Button>
            </DialogActions>
         </Dialog>

         {/* Delete Confirmation Dialog */}
         <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
               <Typography variant='body1'>
                  Are you sure you want to delete the trip "{selectedTrip?.title}"?
               </Typography>
               <Typography variant='body2' color='error' sx={{ mt: 2 }}>
                  This action cannot be undone. All associated locations and collaborator access
                  will be removed.
               </Typography>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
               <Button onClick={handleConfirmDelete} color='error' variant='contained'>
                  Delete
               </Button>
            </DialogActions>
         </Dialog>

         {/* Add Location Dialog */}
         {renderLocationDialog()}

         {/* Invite Collaborator Dialog */}
         <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
               <Box display="flex" alignItems="center">
                  <ShareIcon sx={{ mr: 1 }} />
                  Invite to "{selectedTrip?.title}"
               </Box>
            </DialogTitle>
            <DialogContent>
               <Tabs 
                  value={inviteTabValue} 
                  onChange={handleInviteTabChange}
                  sx={{ mb: 2 }}
               >
                  <Tab 
                     icon={<EmailIcon fontSize="small" />} 
                     label="Email Invite" 
                     id="email-tab"
                     aria-controls="email-panel"
                  />
                  <Tab 
                     icon={<PhoneIcon fontSize="small" />} 
                     label="SMS Invite" 
                     id="sms-tab"
                     aria-controls="sms-panel"
                  />
               </Tabs>
               
               {inviteTabValue === 0 ? (
                  // Email invite form
                  <Box>
                     <TextField
                        autoFocus
                        margin="dense"
                        label="Email Address"
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="email@example.com"
                        InputProps={{
                           startAdornment: (
                              <InputAdornment position="start">
                                 <EmailIcon />
                              </InputAdornment>
                           ),
                        }}
                     />
                     <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel id="role-select-label">Role</InputLabel>
                        <Select
                           labelId="role-select-label"
                           value={inviteeRole}
                           label="Role"
                           onChange={(e) => setInviteeRole(e.target.value)}
                        >
                           <MenuItem value="viewer">Viewer</MenuItem>
                           <MenuItem value="editor">Editor</MenuItem>
                        </Select>
                     </FormControl>
                     <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                        The invited user will receive an email with a link to join this trip.
                     </Typography>
                  </Box>
               ) : (
                  // SMS invite form
                  <SMSInviteForm 
                     tripId={selectedTrip?.id || ''} 
                     tripTitle={selectedTrip?.title || ''} 
                     onInviteSent={handleSMSInviteSent}
                  />
               )}
            </DialogContent>
            
            {inviteTabValue === 0 && (
               <DialogActions>
                  <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleSendInvite} variant="contained" startIcon={<PersonAddIcon />}>
                     Send Invite
                  </Button>
               </DialogActions>
            )}
         </Dialog>

         {/* Snackbar for notifications */}
         <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
         >
            <Alert 
               onClose={() => setSnackbar({ ...snackbar, open: false })} 
               severity={snackbar.severity}
               sx={{ width: '100%' }}
            >
               {snackbar.message}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default TripManagement;
